import type { BriefingStepRequiredness, BriefingStepStatus } from '@/shared/briefing'
import {
  rohsBriefingStepRequiredness,
  type RohsBriefingStepKey,
} from '@/v2/utils/rohsLegislationBriefing'

export type StaticLegislationBriefingLaw = 'weee' | 'fgas' | 'ped' | 'red'

export function isStaticLegislationBriefingLaw(
  law: string
): law is StaticLegislationBriefingLaw {
  return law === 'weee' || law === 'fgas' || law === 'ped' || law === 'red'
}

export type StaticLegislationBriefingStepAction =
  | 'edit'
  | 'upload'
  | 'generate'
  | 'complete'

export type StaticLegislationBriefingStepConfig = {
  key: RohsBriefingStepKey
  requiredness: BriefingStepRequiredness
  action?: StaticLegislationBriefingStepAction
}

export type StaticLegislationBriefingStep = {
  key: RohsBriefingStepKey
  status: BriefingStepStatus
  requiredness: BriefingStepRequiredness
  showAction: boolean
  action: StaticLegislationBriefingStepAction | null
}

export type StaticLegislationBriefing = {
  steps: StaticLegislationBriefingStep[]
}

const ROHS_STATIC_STEP_ACTIONS: Partial<
  Record<RohsBriefingStepKey, StaticLegislationBriefingStepAction>
> = {
  productDescription: 'edit',
  productIdentification: 'upload',
  manufacturerInformation: 'upload',
  productDrawings: 'upload',
  euDeclaration: 'generate',
  productSignOff: 'complete',
}

const ROHS_STATIC_BRIEFING_STEP_KEYS = [
  'applicability',
  'productDescription',
  'productIdentification',
  'manufacturerInformation',
  'productDrawings',
  'partEvidence',
  'euDeclaration',
  'productSignOff',
] as const satisfies readonly RohsBriefingStepKey[]

export const ROHS_STATIC_BRIEFING_STEPS: StaticLegislationBriefingStepConfig[] =
  ROHS_STATIC_BRIEFING_STEP_KEYS.map((key) => ({
    key,
    requiredness: rohsBriefingStepRequiredness(key),
    ...(ROHS_STATIC_STEP_ACTIONS[key] != null
      ? { action: ROHS_STATIC_STEP_ACTIONS[key] }
      : {}),
  }))

export const WEEE_STATIC_BRIEFING_STEPS = ROHS_STATIC_BRIEFING_STEPS
export const FGAS_STATIC_BRIEFING_STEPS = ROHS_STATIC_BRIEFING_STEPS
export const PED_STATIC_BRIEFING_STEPS = ROHS_STATIC_BRIEFING_STEPS

export function buildStaticLegislationBriefing(
  steps: StaticLegislationBriefingStepConfig[]
): StaticLegislationBriefing {
  return {
    steps: steps.map((step) => ({
      key: step.key,
      requiredness: step.requiredness,
      status: step.key === 'applicability' ? 'done' : 'open',
      action: step.action ?? null,
      showAction: step.action != null,
    })),
  }
}

export const STATIC_LEGISLATION_BRIEFING_ID_PREFIX: Record<
  StaticLegislationBriefingLaw,
  string
> = {
  weee: 'weee',
  fgas: 'fgas',
  ped: 'ped',
  red: 'red',
}

export function getStaticLegislationBriefingSteps(
  _law: StaticLegislationBriefingLaw
): StaticLegislationBriefingStepConfig[] {
  return ROHS_STATIC_BRIEFING_STEPS
}
